import { useState, useMemo, useEffect, useEffectEvent } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  CheckCircle2,
  Users,
  Calendar,
  FileText,
  Eye,
  XCircle,
  Loader2,
  AlertTriangle,
  Sparkles,
  Info,
  Search,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import {
  useClientesConHonorarios,
  useClientesConMovimiento,
  useGenerarMovimientosEnLote,
  usePrevisualizarGeneracion
} from '@/hooks/useMovimientos'
import { formatCurrency } from '@/lib/utils'
import { emailService } from '@/services/emailService'
import { useAuth0 } from '@auth0/auth0-react'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { toast } from 'sonner'
import { LoteConcurrenteError, type ResultadoGeneracionLote } from '@/services/honorariosService'

interface GenerarLoteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// Generar opciones de mes/año (6 meses atrás + actual + 2 adelante)
function generarOpcionesMesAnio(): { value: string; label: string; esMesActual: boolean }[] {
  const opciones: { value: string; label: string; esMesActual: boolean }[] = []
  const ahora = new Date()

  for (let i = -6; i <= 2; i++) {
    const fecha = new Date(ahora.getFullYear(), ahora.getMonth() + i, 1)
    const mes = fecha.toLocaleString('es-AR', { month: 'long' })
    const anio = fecha.getFullYear()
    const value = `${mes.charAt(0).toUpperCase() + mes.slice(1)} ${anio}`
    opciones.push({ value, label: value, esMesActual: i === 0 })
  }

  return opciones
}

export function GenerarLoteDialog({ open, onOpenChange }: GenerarLoteDialogProps) {
  const { user, getAccessTokenSilently } = useAuth0()
  const { organizacionId } = useOrganizacion()
  const opcionesMes = useMemo(() => generarOpcionesMesAnio(), [])

  // Estado del formulario
  const [mesAnio, setMesAnio] = useState(() => {
    const mesActual = opcionesMes.find(op => op.esMesActual)
    return mesActual?.value ?? opcionesMes[0]?.value ?? ''
  })
  const [concepto, setConcepto] = useState('Honorarios {servicio} - {periodo}')
  const [fecha, setFecha] = useState(() => {
    const d = new Date()
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  })
  const [clientesSeleccionados, setClientesSeleccionados] = useState<string[]>([])
  const [seleccionarTodos, setSeleccionarTodos] = useState(true)

  // Estado para previsualización
  const [previsualizacion, setPrevisualizacion] = useState<ResultadoGeneracionLote['detalle'] | null>(null)
  // Clientes destildados en la previsualización. Es estado propio de esa pantalla: volver atrás
  // lo descarta y deja la selección del paso de configuración como estaba.
  const [clientesExcluidos, setClientesExcluidos] = useState<Set<string>>(new Set())
  const [busqueda, setBusqueda] = useState('')
  const [mostrarOmitidos, setMostrarOmitidos] = useState(false)

  // Estado para búsqueda en selección de clientes
  const [busquedaClientes, setBusquedaClientes] = useState('')

  // Estado para confirmación de cierre
  const [mostrarConfirmacionCerrar, setMostrarConfirmacionCerrar] = useState(false)

  // Queries - Usar el nuevo hook que considera servicios
  const { data: clientesConHonorarios = [], isLoading: loadingClientes } = useClientesConHonorarios()
  const { data: clientesConMovimiento = [], isLoading: loadingExistentes } = useClientesConMovimiento(mesAnio)

  // Mutations
  const generarLote = useGenerarMovimientosEnLote()
  const previsualizar = usePrevisualizarGeneracion()

  // Only show clients that have at least one recurring service (not 'unico').
  // The backend handles per-service deduplication for partial billing.
  const clientesDisponibles = useMemo(() => {
    return clientesConHonorarios.filter(c =>
      c.cliente_id &&
      c.servicios.some(s => s.frecuencia !== 'unico')
    )
  }, [clientesConHonorarios])

  const clientesConMovimientoParcial = useMemo(() => {
    return clientesConHonorarios.filter(c => c.cliente_id && clientesConMovimiento.includes(c.cliente_id))
  }, [clientesConHonorarios, clientesConMovimiento])

  // Clients filtered by search query (for selection list)
  const clientesFiltradosBusqueda = useMemo(() => {
    if (!busquedaClientes.trim()) return clientesDisponibles
    const q = busquedaClientes.toLowerCase()
    return clientesDisponibles.filter(c =>
      c.cliente_nombre.toLowerCase().includes(q)
    )
  }, [clientesDisponibles, busquedaClientes])

  // Detectar si hay datos que podrían perderse
  // Siempre pedir confirmación si hay clientes disponibles o previsualización
  const tieneDatosParaConfirmar = useMemo(() => {
    return previsualizacion !== null || clientesDisponibles.length > 0
  }, [previsualizacion, clientesDisponibles.length])

  // Reset al cerrar
  const resetOnClose = useEffectEvent(() => {
    setClientesSeleccionados([])
    setSeleccionarTodos(true)
    setPrevisualizacion(null)
    setClientesExcluidos(new Set())
    setBusqueda('')
    setBusquedaClientes('')
    setMostrarOmitidos(false)
  })
  useEffect(() => {
    if (!open) resetOnClose()
  }, [open])

  // Filtrar previsualización por búsqueda y separar en creados / omitidos
  const previsualizacionFiltrada = useMemo(() => {
    if (!previsualizacion) return []
    const q = busqueda.toLowerCase()
    return previsualizacion.filter(item => item.cliente_nombre.toLowerCase().includes(q))
  }, [previsualizacion, busqueda])

  const creadosFiltrados = useMemo(
    () => previsualizacionFiltrada.filter(item => item.estado === 'creado'),
    [previsualizacionFiltrada]
  )

  const omitidosFiltrados = useMemo(
    () => previsualizacionFiltrada.filter(item => item.estado === 'omitido'),
    [previsualizacionFiltrada]
  )

  // Clientes de la previsualización que entran al lote: los creados que siguen tildados.
  // Se calcula sobre la previsualización completa, no sobre la vista filtrada por búsqueda.
  const creadosAGenerar = useMemo(
    () => (previsualizacion ?? []).filter(
      item => item.estado === 'creado' && !clientesExcluidos.has(item.cliente_id)
    ),
    [previsualizacion, clientesExcluidos]
  )

  // Calcular totales desde previsualización.
  //
  // Un cliente con dos servicios mensuales produce dos movimientos, así que la cantidad de filas
  // de la previsualización es la de clientes, no la de movimientos: la previsualización trae
  // cuántos le corresponden a cada uno. Las filas viejas, sin ese dato, cuentan como uno.
  const totalesPreview = useMemo(() => ({
    clientes: creadosAGenerar.length,
    cantidad: creadosAGenerar.reduce((sum, item) => sum + (item.movimientos ?? 1), 0),
    monto: creadosAGenerar.reduce((sum, item) => sum + (item.monto ?? 0), 0)
  }), [creadosAGenerar])

  const cantidadAGenerar = seleccionarTodos
    ? clientesDisponibles.length
    : clientesSeleccionados.length

  // Handlers
  const handleToggleCliente = (clienteId: string) => {
    setClientesSeleccionados(prev =>
      prev.includes(clienteId)
        ? prev.filter(id => id !== clienteId)
        : [...prev, clienteId]
    )
  }

  // Tilde de una fila de la previsualización: destildado significa que ese cliente no entra al lote.
  const handleToggleExclusion = (clienteId: string) => {
    setClientesExcluidos(prev => {
      const siguiente = new Set(prev)
      if (!siguiente.delete(clienteId)) siguiente.add(clienteId)
      return siguiente
    })
  }

  const handleSeleccionarTodos = (checked: boolean) => {
    setSeleccionarTodos(checked)
    if (checked) {
      setClientesSeleccionados([])
    }
  }

  // Manejar intento de cerrar
  const handleIntentarCerrar = (isOpen: boolean) => {
    if (!isOpen && tieneDatosParaConfirmar) {
      setMostrarConfirmacionCerrar(true)
    } else {
      onOpenChange(isOpen)
    }
  }

  const confirmarCerrar = () => {
    setMostrarConfirmacionCerrar(false)
    onOpenChange(false)
  }

  const handlePrevisualizar = async () => {
    // Validaciones
    if (!mesAnio || !concepto || !fecha) {
      toast.error('Completa todos los campos requeridos')
      return
    }

    if (!seleccionarTodos && clientesSeleccionados.length === 0) {
      toast.error('Selecciona al menos un cliente')
      return
    }

    // Validar fecha no futura
    const fechaSeleccionada = new Date(fecha)
    const hoy = new Date()
    hoy.setHours(0, 0, 0, 0)

    if (fechaSeleccionada > hoy) {
      toast.error('La fecha no puede ser futura')
      return
    }

    try {
      const detalle = await previsualizar.mutateAsync({
        mesAnio,
        clienteIds: seleccionarTodos ? undefined : clientesSeleccionados,
      })

      setPrevisualizacion(detalle)
      // Los clientes con un cargo manual equivalente arrancan destildados; el usuario puede volver a tildarlos.
      setClientesExcluidos(new Set(detalle.filter(d => d.posibleDuplicado).map(d => d.cliente_id)))
      const creados = detalle.filter(d => d.estado === 'creado')
      const movimientos = creados.reduce((sum, item) => sum + (item.movimientos ?? 1), 0)
      toast.success(
        `Previsualización generada: ${movimientos} movimientos para ${creados.length} clientes`
      )
    } catch (error) {
      toast.error('Error al generar la previsualización')
      console.error(error)
    }
  }

  const handleGenerar = async () => {
    if (!previsualizacion) {
      toast.error('Primero debes previsualizar la generación')
      return
    }

    if (totalesPreview.cantidad === 0) {
      toast.error('No hay movimientos para generar')
      return
    }

    // Con alguna fila destildada manda la lista de los que quedaron; sin exclusiones respeta la
    // selección del paso de configuración, para no cambiar el lote de quien no destildó nada.
    const clienteIds = clientesExcluidos.size > 0
      ? creadosAGenerar.map(item => item.cliente_id)
      : seleccionarTodos ? undefined : clientesSeleccionados

    try {
      const resultado = await generarLote.mutateAsync({
        mesAnio,
        concepto,
        fecha,
        clienteIds,
        loteCompleto: seleccionarTodos,
      })

      toast.success(
        `Se generaron ${resultado.creados} movimientos por ${formatCurrency(resultado.montoTotal)}. ${resultado.omitidos > 0 ? `(${resultado.omitidos} omitidos)` : ''}`
      )

      // Enviar email de resumen al usuario
      if (user?.email && organizacionId && resultado.creados > 0) {
        try {
          const token = await getAccessTokenSilently()
          await emailService.enviarResumenMovimientos(
            token,
            [user.email],
            {
              clientesCreados: resultado.creados,
              montoTotal: resultado.montoTotal,
              clientesDuplicados: resultado.omitidos,
              clientesOmitidos: clientesConMovimientoParcial.length
            },
            mesAnio,
            false,
            organizacionId
          )
          toast.success('Resumen enviado a tu email')
        } catch (emailError) {
          console.error('Error enviando email de resumen:', emailError)
        }
      }

      onOpenChange(false)
    } catch (error) {
      toast.error(error instanceof LoteConcurrenteError ? error.message : 'Error al generar los movimientos')
      console.error(error)
    }
  }

  const handleCancelarPreview = () => {
    setPrevisualizacion(null)
    setClientesExcluidos(new Set())
    setBusqueda('')
    setMostrarOmitidos(false)
  }

  const isLoading = loadingClientes || loadingExistentes

  return (
    <>
      <Sheet open={open} onOpenChange={handleIntentarCerrar}>
        <SheetContent side="right" className="w-full sm:max-w-xl md:max-w-2xl flex flex-col p-0">
          {/* Header */}
          <SheetHeader className="px-6 pt-6 pb-4 border-b bg-muted/30">
            <SheetTitle className="flex items-center gap-2 text-lg">
              <Calendar className="h-5 w-5 text-primary" />
              {previsualizacion ? 'Previsualización' : 'Generar Movimientos en Lote'}
            </SheetTitle>
            <SheetDescription>
              {previsualizacion
                ? `Revisa el detalle para ${mesAnio} antes de confirmar`
                : 'Genera honorarios para todos los clientes activos'
              }
            </SheetDescription>
          </SheetHeader>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {!previsualizacion ? (
              <div className="p-6 space-y-6">
                {/* Sección: Configuración del Período */}
                <section className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    Configuración del período
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="periodo">Período</Label>
                      <Select value={mesAnio} onValueChange={setMesAnio}>
                        <SelectTrigger id="periodo">
                          <SelectValue placeholder="Seleccionar período" />
                        </SelectTrigger>
                        <SelectContent>
                          {opcionesMes.map(op => (
                            <SelectItem key={op.value} value={op.value}>
                              {op.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="fecha">Fecha del movimiento</Label>
                      <Input
                        id="fecha"
                        type="date"
                        value={fecha}
                        onChange={(e) => setFecha(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="concepto" className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Concepto
                    </Label>
                    <Input
                      id="concepto"
                      value={concepto}
                      onChange={(e) => setConcepto(e.target.value)}
                      placeholder="Ej: Honorarios {periodo}"
                    />
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Info className="h-3 w-3" />
                      Usa {'{periodo}'} para el mes/año, {'{cliente}'} para el nombre y {'{servicio}'} para el servicio
                    </p>
                  </div>
                </section>

                <Separator />

                {/* Sección: Resumen de clientes */}
                <section className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Users className="h-4 w-4" />
                    Resumen de clientes
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-xl border bg-card p-4 text-center">
                      <p className="text-2xl font-bold">{clientesConHonorarios.length}</p>
                      <p className="text-xs text-muted-foreground mt-1">Total activos</p>
                    </div>
                    <div className="rounded-xl border p-4 text-center border-primary/20 bg-primary/5">
                      <p className="text-2xl font-bold text-primary">{cantidadAGenerar}</p>
                      <p className="text-xs text-muted-foreground mt-1">A generar</p>
                    </div>
                    <div className="rounded-xl border p-4 text-center border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950">
                      <p className="text-2xl font-bold text-green-600">{clientesDisponibles.length}</p>
                      <p className="text-xs text-muted-foreground mt-1">Disponibles</p>
                    </div>
                  </div>

                  {/* Alerta de clientes ya facturados */}
                  {clientesConMovimientoParcial.length > 0 && (
                    <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950">
                      <Info className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                          {clientesConMovimientoParcial.length} cliente(s) ya tienen algunos movimientos para {mesAnio}
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                          Solo se generarán los servicios pendientes. Los ya facturados se omiten automáticamente.
                        </p>
                      </div>
                    </div>
                  )}
                </section>

                <Separator />

                {/* Sección: Selección de clientes */}
                <section className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Sparkles className="h-4 w-4" />
                      Clientes a facturar
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="todos"
                        checked={seleccionarTodos}
                        onCheckedChange={handleSeleccionarTodos}
                      />
                      <label htmlFor="todos" className="text-sm cursor-pointer">
                        Todos ({clientesDisponibles.length})
                      </label>
                    </div>
                  </div>

                  {!seleccionarTodos && (
                    <div className="border rounded-lg">
                      {/* Search input - fixed above scroll */}
                      <div className="p-2 border-b">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Buscar cliente..."
                            value={busquedaClientes}
                            onChange={(e) => setBusquedaClientes(e.target.value)}
                            className="pl-9"
                          />
                        </div>
                      </div>

                      <ScrollArea className="h-[200px]">
                        <div className="p-2 space-y-1">
                          {isLoading ? (
                            <div className="flex items-center justify-center h-40 text-muted-foreground">
                              <Loader2 className="h-5 w-5 animate-spin mr-2" />
                              Cargando clientes...
                            </div>
                          ) : clientesFiltradosBusqueda.length === 0 ? (
                            <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
                              {busquedaClientes ? 'Sin resultados' : 'No hay clientes disponibles'}
                            </div>
                          ) : (
                            clientesFiltradosBusqueda.map(cliente => (
                              <div
                                key={cliente.cliente_id}
                                className={`flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer transition-colors ${
                                  clientesSeleccionados.includes(cliente.cliente_id) ? 'bg-primary/5' : ''
                                }`}
                                onClick={() => handleToggleCliente(cliente.cliente_id)}
                              >
                                <Checkbox
                                  checked={clientesSeleccionados.includes(cliente.cliente_id)}
                                  onCheckedChange={() => handleToggleCliente(cliente.cliente_id)}
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{cliente.cliente_nombre}</p>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {cliente.servicios.filter(s => s.frecuencia !== 'unico').length} servicio(s) recurrente(s)
                                  </p>
                                </div>
                                <Badge variant="secondary" className="shrink-0">
                                  {formatCurrency(cliente.honorarios_total)}
                                </Badge>
                              </div>
                            ))
                          )}
                        </div>
                      </ScrollArea>

                      {/* Selection summary */}
                      {clientesSeleccionados.length > 0 && (
                        <div className="p-2 border-t text-xs text-muted-foreground text-center">
                          {clientesSeleccionados.length} de {clientesDisponibles.length} seleccionado(s)
                        </div>
                      )}
                    </div>
                  )}

                  {seleccionarTodos && clientesDisponibles.length > 0 && (
                    <div className="flex items-center gap-3 p-4 rounded-lg bg-primary/5 border border-primary/20">
                      <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                      <p className="text-sm">
                        Se previsualizarán movimientos para <strong>{clientesDisponibles.length}</strong> clientes
                      </p>
                    </div>
                  )}
                </section>
              </div>
            ) : (
              /* Vista de Previsualización */
              <div className="p-6 space-y-4">
                {/* Búsqueda */}
                <Input
                  placeholder="Buscar cliente..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />

                {/* Resumen destacado */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900 p-4 text-center border-green-200 dark:border-green-800">
                    <p className="text-3xl font-bold text-green-700 dark:text-green-300">{totalesPreview.cantidad}</p>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                      Movimientos a crear
                    </p>
                    <p className="text-xs text-green-600/80 dark:text-green-400/80">
                      para {totalesPreview.clientes} cliente{totalesPreview.clientes === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="rounded-xl border bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 p-4 text-center border-blue-200 dark:border-blue-800">
                    <p className="text-3xl font-bold text-blue-700 dark:text-blue-300">{formatCurrency(totalesPreview.monto)}</p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Monto total</p>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Info className="h-3 w-3 shrink-0" />
                  Destildá los clientes a los que no les querés generar la deuda de este período
                </p>

                {/* Tabla de previsualización */}
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-10">Incluir</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead className="text-right">Monto</TableHead>
                      </TableRow>
                    </TableHeader>
                  </Table>
                  <ScrollArea className="h-[280px]">
                    <Table>
                      <TableBody>
                        {creadosFiltrados.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                              No hay movimientos a crear
                            </TableCell>
                          </TableRow>
                        ) : (
                          creadosFiltrados.map((item) => {
                            const incluido = !clientesExcluidos.has(item.cliente_id)
                            return (
                              <TableRow
                                key={item.cliente_id}
                                onClick={() => handleToggleExclusion(item.cliente_id)}
                                className={`cursor-pointer ${incluido ? '' : 'opacity-50'}`}
                              >
                                <TableCell className="w-10">
                                  <Checkbox
                                    checked={incluido}
                                    onCheckedChange={() => handleToggleExclusion(item.cliente_id)}
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label={`Incluir a ${item.cliente_nombre}`}
                                  />
                                </TableCell>
                                <TableCell>
                                  <p className="font-medium truncate">{item.cliente_nombre}</p>
                                  {item.posibleDuplicado && (
                                    <p className="text-xs text-muted-foreground">
                                      Ya tiene un cargo manual de {formatCurrency(item.posibleDuplicado.monto)} en este mes (
                                      {item.posibleDuplicado.servicio}). Tildalo si corresponde cobrarlo igual.
                                    </p>
                                  )}
                                </TableCell>
                                <TableCell className="text-right">
                                  <span className={`font-semibold ${incluido ? 'text-green-600' : 'text-muted-foreground line-through'}`}>
                                    {formatCurrency(item.monto ?? 0)}
                                  </span>
                                </TableCell>
                              </TableRow>
                            )
                          })
                        )}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </div>

                {/* Omitidos — sección colapsable (colapsada por defecto) */}
                {omitidosFiltrados.length > 0 && (
                  <div className="rounded-lg border border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950">
                    <button
                      type="button"
                      onClick={() => setMostrarOmitidos(v => !v)}
                      className="flex w-full items-center gap-3 p-3 text-left"
                      aria-expanded={mostrarOmitidos}
                    >
                      <AlertTriangle className="h-5 w-5 text-orange-600 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                          {omitidosFiltrados.length} cliente(s) no se facturan en este lote
                        </p>
                        <p className="text-xs text-orange-700 dark:text-orange-300 mt-0.5">
                          {mostrarOmitidos ? 'Ocultar el detalle' : 'Ver el detalle y las razones'}
                        </p>
                      </div>
                      {mostrarOmitidos ? (
                        <ChevronUp className="h-4 w-4 text-orange-600 shrink-0" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-orange-600 shrink-0" />
                      )}
                    </button>
                    {mostrarOmitidos && (
                      <div className="border-t border-orange-200 divide-y divide-orange-200/60 dark:border-orange-900 dark:divide-orange-900/60">
                        {omitidosFiltrados.map((item) => (
                          <div key={item.cliente_id} className="flex items-center gap-2 px-3 py-2">
                            <XCircle className="h-4 w-4 text-muted-foreground shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm truncate">{item.cliente_nombre}</p>
                              {item.razon && (
                                <p className="text-xs text-muted-foreground truncate">{item.razon}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <SheetFooter className="px-6 py-4 border-t bg-muted/30 gap-2 sm:gap-2">
            {!previsualizacion ? (
              <>
                <Button variant="outline" onClick={() => handleIntentarCerrar(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handlePrevisualizar}
                  disabled={previsualizar.isPending || cantidadAGenerar === 0}
                  className="gap-2"
                >
                  {previsualizar.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Previsualizando...
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4" />
                      Previsualizar ({cantidadAGenerar})
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={handleCancelarPreview}>
                  Volver
                </Button>
                <Button
                  onClick={handleGenerar}
                  disabled={generarLote.isPending || totalesPreview.cantidad === 0}
                  className="gap-2"
                >
                  {generarLote.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Generar {totalesPreview.cantidad} movimientos
                    </>
                  )}
                </Button>
              </>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Diálogo de confirmación al cerrar */}
      <AlertDialog open={mostrarConfirmacionCerrar} onOpenChange={setMostrarConfirmacionCerrar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Descartar cambios?</AlertDialogTitle>
            <AlertDialogDescription>
              Tienes una previsualización en curso. Si cierras ahora, perderás los datos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarCerrar} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Descartar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
